import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ApiHelperFunction from "../../api";
import { initialStateProps } from "./interface";

// ✅ Add Agent Thunk
export const addAgents = createAsyncThunk(
  "admin/create-agent",
  async (payload: any, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: "admin/create-agent/",
      method: "post",
      data: payload,
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data;
  }
);

// ✅ Update Agent Thunk
export const updateAgents = createAsyncThunk(
  "admin/update-user",
  async ({ id, ...payload }: { id: number; [key: string]: any }, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `admin/update-user/${id}`,   // ✅ Append `id` directly in the URL
      method: "put",
      data: payload,   // ✅ Send payload in the request body
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data;
  }
);


// ✅ Delete Agent Thunk (Dynamic ID)
export const deleteAgents = createAsyncThunk(
  "admin/delete-agent",
  async (id: number, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `admin/delete-user/${id}/?type=1`,  
      method: "delete",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data;
  }
);

export const activeInactiveAgents = createAsyncThunk(
    "admin/delete-user",
    async (id: number, { dispatch }) => {
      const token = localStorage.getItem("user_token");
  
      const response = await ApiHelperFunction({
        url: `admin/delete-user/${id}/?type=0`,   
        method: "delete",
        dispatch,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `${token}` : "",
        },
      });
  
      return response?.data;
    }
  );
  
// ✅ Get Agent List Thunk
export const getAgentList = createAsyncThunk(
  "admin/get-all-users",
  async ( { offset = 0, limit = 10, role_id = 3, search = "" }: { offset?: number; limit?: number; role_id?: number , search: ''}, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const queryParams = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
      role_id: role_id.toString(),
    });
    if (search) {
      queryParams.append("search", search);
    }

    const response = await ApiHelperFunction({
      url: `admin/get-all-users/?${queryParams}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return {
      data: response?.data?.data || [],
      total_records: response?.data?.total_records || 0,
    };
  }
);


// ✅ Get Single Agent Thunk
export const getSingleAgent = createAsyncThunk(
  "admin/get-user",  // Action type
  async (id: number, { dispatch }) => {   // Accept `id` as an argument
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `admin/get-user/${id}`,     // Dynamically inject `id` into the endpoint
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data?.data || {};
  }
);


// ✅ Initial State
export const initialState: initialStateProps = {
  status: "idle",
  error: "",
  token: "",
  data: [],
  total_records:0,
  records_limit:10,

};export const agentSlice = createSlice({
  name: "agents",
  initialState,
  reducers: {
    resetAll: (state) => {
      state.error = "";
      state.data = [];
      state.total_records=0;
      state.records_limit=10;
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Add Agents
      .addCase(addAgents.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addAgents.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = [...state.data, action.payload?.user];
      })
      .addCase(addAgents.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

       // ✅ Update Agents
       .addCase(updateAgents.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateAgents.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = [...state.data, action.payload?.user];
      })
      .addCase(updateAgents.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Delete Agents
      .addCase(deleteAgents.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteAgents.fulfilled, (state, action) => {
        state.status = "succeeded";
        const deletedId = action.meta.arg;  // Get the ID from the action
        state.data = state.data.filter((agent: any) => agent.id !== deletedId);
      })
      .addCase(deleteAgents.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Active/Inactive Agents (now using a unique action type)
      .addCase(activeInactiveAgents.pending, (state) => {
        state.status = "loading";
      })
      
      .addCase(activeInactiveAgents.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Get Agent List
      .addCase(getAgentList.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAgentList.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload.data;  // Set the data array
        state.total_records = action.payload.total_records;  // Set the total_records properly
      })
      .addCase(getAgentList.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});
